import { getDefaultNodeImage, getNodeImage, getNodeName } from "Areas/SharedFeatures/nodeLabel";
import { COLUMN } from "Areas/SharedFeatures/rightSideDetailsPanel";
import useBeforeUnloadMessage from "Common/componentUtils/useBeforeUnloadMessage";
import { OPTION_TYPE } from "Common/dictionaries/optionType";
import { SUMMARY_REPORT_OBJECT_TYPE } from "Common/dictionaries/summaryReportObjectType";
import { getItem, setItem } from "Common/localStorage/utils";
import ProgressOverlay from "Components/progressOverlay";
import classNames from "classnames";
import cloneDeep from "lodash/cloneDeep";
import filter from "lodash/filter";
import find from "lodash/find";
import findIndex from "lodash/findIndex";
import forEach from "lodash/forEach";
import includes from "lodash/includes";
import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import isUndefined from "lodash/isUndefined";
import keyBy from "lodash/keyBy";
import map from "lodash/map";
import pick from "lodash/pick";
import values from "lodash/values";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { READ_ONLY_MODE } from "../documentDetails/types/readOnlyMode";
import { localStorageSettingsKey } from "./constants";
import DocumentObjectTypesPanel from "./documentObjectTypesPanel";
import RightSideDetailsPanel from "./rightSideDetailsPanel";
import RowActionsPanel from "./rowActionsPanel";
import TableInner from "./table.inner";
import Welcome from "./welcome";
import { DOCUMENT_BG } from "../constants";
const detailsPanelTableColumns = [COLUMN.OBJECT_TYPE, COLUMN.ID, COLUMN.COLOR];
const SIZES = {
    DOCUMENT_OBJECT_TYPES_PANEL_HEIGHT: 40
};
export default function Table(props) {
    const { height, ref: observableRef } = useResizeDetector();
    const gridRef = useRef(null);
    const hasChangesRef = useRef(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [isColumnChooserOpen, setColumnChooserOpen] = useState(false);
    const handleShown = useRef(() => setColumnChooserOpen(true));
    const handleHidden = useRef(() => setColumnChooserOpen(false));
    const [settings, setSettings] = useState(() => {
        const localStorageData = getItem(localStorageSettingsKey);
        return {
            alignment: (localStorageData === null || localStorageData === void 0 ? void 0 : localStorageData.alignment) || "text-left",
            fontSize: (localStorageData === null || localStorageData === void 0 ? void 0 : localStorageData.fontSize) || "font-base",
            hiddenColumns: isArray(localStorageData === null || localStorageData === void 0 ? void 0 : localStorageData.hiddenColumns) ? localStorageData === null || localStorageData === void 0 ? void 0 : localStorageData.hiddenColumns : []
        };
    });
    const documentNodeAttributeImageRef = useRef({});
    const tableRef = useRef(null);
    const nodeSourcesRef = useRef(props.nodeSources);
    const initialNodeSourcesRef = useRef(props.initialNodeSources);
    const readOnlyModeRef = useRef(props.readOnlyMode);
    useEffect(() => {
        hasChangesRef.current = hasChanges;
    }, [hasChanges]);
    useEffect(() => {
        nodeSourcesRef.current = props.nodeSources;
    }, [props.nodeSources]);
    useEffect(() => {
        initialNodeSourcesRef.current = props.initialNodeSources;
    }, [props.initialNodeSources]);
    useEffect(() => {
        readOnlyModeRef.current = props.readOnlyMode;
    }, [props.readOnlyMode]);
    useBeforeUnloadMessage(hasChangesRef);
    useLayoutEffect(() => {
        setHasChanges(false);
    }, [props.documentId, props.columns]);
    const handleChangeSettings = (field, value) => {
        setSettings(prev => ({
            ...prev,
            [field]: value
        }));
    };
    const savingCallback = useCallback((documentId) => () => {
        if (initialNodeSourcesRef.current &&
            nodeSourcesRef.current &&
            hasChangesRef.current &&
            props.canEdit &&
            readOnlyModeRef.current === READ_ONLY_MODE.READ_WRITE &&
            props.onSaveTableData) {
            const data = values(nodeSourcesRef.current);
            props.onChangeNodeSources && props.onChangeNodeSources(documentId, data, false, true);
            props.onSaveTableData(documentId, values(initialNodeSourcesRef.current), data);
            setHasChanges(false);
        }
    }, [props.onSaveTableData, props.onChangeNodeSources]);
    useEffect(() => {
        props.onInitialized && props.onInitialized();
    }, []);
    useEffect(() => {
        props.documentId &&
            props.onGetAvailableDocumentObjectIds &&
            props.onGetAvailableDocumentObjectIds(props.documentId);
        return () => {
            props.documentId && savingCallback(props.documentId)();
        };
    }, [props.documentId]);
    useEffect(() => {
        props.documentId &&
            !props.data &&
            props.availableDocumentObjectIds &&
            !props.availableDocumentObjectIds.length &&
            props.onGetTableData &&
            props.onGetTableData(props.documentId);
    }, [props.documentId, props.availableDocumentObjectIds, props.data]);
    const handleGetTableData = useCallback((selectedDocumentObjectIds) => {
        if (!props.documentId)
            return;
        if (selectedDocumentObjectIds)
            props.onSelectDocumentObjects &&
                props.onSelectDocumentObjects(props.documentId, selectedDocumentObjectIds);
        (selectedDocumentObjectIds || props.selectedDocumentObjectIds) &&
            props.onGetTableData &&
            props.onGetTableData(props.documentId, selectedDocumentObjectIds || props.selectedDocumentObjectIds);
    }, [
        props.documentId,
        props.selectedDocumentObjectIds,
        props.onGetTableData,
        props.onSelectDocumentObjects
    ]);
    const handleSelectDocumentObjects = useCallback((documentObjectIds) => {
        props.documentId &&
            props.onSelectDocumentObjects &&
            props.onSelectDocumentObjects(props.documentId, documentObjectIds);
    }, [props.documentId, props.onSelectDocumentObjects]);
    useEffect(() => {
        setItem(localStorageSettingsKey, settings);
    }, [settings]);
    const handleGetPreview = useCallback((_, nodeId, attributeId, largeAttributeId, completed, rejected) => {
        const value = documentNodeAttributeImageRef.current[nodeId]
            ? documentNodeAttributeImageRef.current[nodeId][attributeId]
            : undefined;
        if (value) {
            completed(value);
        }
        else if (largeAttributeId) {
            props.onGetPreview(largeAttributeId, completed, rejected);
        }
        else {
            rejected && rejected();
        }
    }, [props.onGetPreview]);
    const handleDataUpdate = useCallback((nodeIds, fieldId, value, attributeType) => {
        const updatedData = [];
        const updatedRows = [];
        if (!props.nodeSources || !props.documentId)
            return updatedData;
        forEach(Array.from(nodeIds), nodeId => {
            var _a;
            if (!props.nodeSources || !props.nodeSources[nodeId])
                return;
            const nodeData = cloneDeep(props.nodeSources[nodeId]);
            const row = find(props.data, ["Id", nodeId]);
            const columnIndex = (_a = tableRef.current) === null || _a === void 0 ? void 0 : _a.getColumnIndex(nodeId, fieldId);
            if (row && !isUndefined(columnIndex)) {
                const updatedRow = cloneDeep(row);
                updatedRow.Values[columnIndex] = value;
                updatedRows.push(updatedRow);
            }
            if (attributeType) {
                if (!nodeData.AttributeValues)
                    nodeData.AttributeValues = [];
                const prevIndex = findIndex(nodeData.AttributeValues, ["Key", fieldId]);
                if (prevIndex === -1)
                    !isNil(value) &&
                        nodeData.AttributeValues.push({
                            Key: fieldId,
                            Value: value
                        });
                else if (isNil(value))
                    nodeData.AttributeValues.splice(prevIndex, 1);
                else
                    nodeData.AttributeValues[prevIndex].Value = value;
                const documentObject = props.documentObjects
                    ? props.documentObjects[nodeData.DocumentObjectId]
                    : undefined;
                if (attributeType !== OPTION_TYPE.IMAGE) {
                    nodeData.NodeName = getNodeName(nodeData.AttributeValues, documentObject);
                }
                else {
                    nodeData.NodeImage = getNodeImage(nodeData.AttributeValues, documentObject, true);
                    const defaultNodeImage = getDefaultNodeImage(documentObject);
                    nodeData.NodeDefaultImage =
                        defaultNodeImage !== nodeData.NodeImage ? defaultNodeImage : undefined;
                    const imgValue = value;
                    if (!documentNodeAttributeImageRef.current[nodeData.NodeId])
                        documentNodeAttributeImageRef.current[nodeData.NodeId] = {};
                    documentNodeAttributeImageRef.current[nodeData.NodeId][fieldId] = imgValue;
                }
            }
            else {
                nodeData[fieldId] = value;
            }
            updatedData.push(nodeData);
        });
        props.onChangeNodeSources &&
            props.onChangeNodeSources(props.documentId, updatedData, true, false);
        props.onChangeTableData &&
            updatedRows.length &&
            props.onChangeTableData(props.documentId, updatedRows);
        if (updatedData.length) {
            setHasChanges(true);
        }
        return updatedData;
    }, [
        props.documentId,
        props.documentObjects,
        props.data,
        props.nodeSources,
        props.onChangeNodeSources,
        props.onChangeTableData
    ]);
    const handleSetHiddenColumns = useCallback((hiddenColumns) => {
        setSettings(prev => ({ ...prev, hiddenColumns }));
    }, []);
    const dataGrid = useMemo(() => props.data &&
        props.columns && (React.createElement(TableInner, { gridRef: gridRef, tableRef: tableRef, documentId: props.documentId, documentObjects: props.documentObjects, data: props.data, columns: props.columns, height: height, settings: settings, onSetHiddenColumns: handleSetHiddenColumns, onGetPreview: handleGetPreview, onChangeSelected: props.onChangeSelected, selected: props.selectedRowIds })), [
        props.documentId,
        props.documentObjects,
        props.data,
        props.columns,
        settings,
        handleSetHiddenColumns,
        handleGetPreview,
        props.onChangeSelected,
        height
    ]);
    const containerStyle = useMemo(() => ({ paddingBottom: props.bottomOffset, ...DOCUMENT_BG }), [props.bottomOffset]);
    const getSummaryReportObjectValue = useCallback(async (summaryReportObjectType) => {
        var _a;
        if (!gridRef.current || !((_a = props.selectedRowIds) === null || _a === void 0 ? void 0 : _a.length) || !props.nodeSources)
            return undefined;
        if (summaryReportObjectType === SUMMARY_REPORT_OBJECT_TYPE.DOCUMENT_OBJECT_NODES) {
            const selectedNodeSources = values(pick(props.nodeSources, props.selectedRowIds));
            return selectedNodeSources.length ? selectedNodeSources : undefined;
        }
        if (summaryReportObjectType === SUMMARY_REPORT_OBJECT_TYPE.TABLE_ROWS) {
            const selectedRows = filter(props.data, d => includes(props.selectedRowIds, d.Id));
            if (!selectedRows.length)
                return undefined;
            const visibleColumns = filter(gridRef.current.instance.getVisibleColumns(), "dataField");
            const columnsDataFields = map(visibleColumns, "dataField");
            const includedColumnsIndexes = [];
            const dataFieldToColumnMap = keyBy(props.columns, "Key");
            forEach(props.columns, (c, ind) => {
                if (includes(columnsDataFields, c.Key))
                    includedColumnsIndexes.push(ind);
            });
            if (!includedColumnsIndexes.length)
                return undefined;
            const rows = map(selectedRows, r => ({
                ...r,
                Values: filter(r.Values, (_, ind) => includes(includedColumnsIndexes, ind))
            }));
            if (!rows.length)
                return undefined;
            return {
                Headers: map(visibleColumns, c => ({
                    Name: c.caption,
                    Type: c.dataField && dataFieldToColumnMap[c.dataField]
                        ? dataFieldToColumnMap[c.dataField].Type
                        : OPTION_TYPE.STRING
                })),
                Rows: rows
            };
        }
        return undefined;
    }, [props.nodeSources, props.data, props.selectedRowIds, props.columns]);
    const handleOpenColumnChooser = useCallback(() => {
        var _a, _b, _c;
        if (gridRef.current) {
            (_a = gridRef.current) === null || _a === void 0 ? void 0 : _a.instance.showColumnChooser();
            const popup = (_c = (_b = gridRef.current.instance) === null || _b === void 0 ? void 0 : _b.getView("columnChooserView")) === null || _c === void 0 ? void 0 : _c._popupContainer;
            if (popup) {
                popup.option("hideOnOutsideClick", true);
                popup.option("dragEnabled", false);
                popup.option("animation", undefined);
                const position = popup.option("position");
                popup.option("position", {
                    ...(position || {}),
                    my: "left top",
                    at: "left top",
                    offset: "0 -81"
                });
                popup.off("shown", handleShown.current);
                popup.off("hidden", handleHidden.current);
                popup.on("shown", handleShown.current);
                popup.on("hidden", handleHidden.current);
            }
        }
    }, []);
    const handleCancelGetTableData = useCallback(() => {
        props.documentId && props.onCancelTableData && props.onCancelTableData(props.documentId);
    }, [props.documentId, props.onCancelTableData]);
    useEffect(() => {
        props.onInProgressChanged &&
            props.onInProgressChanged(Boolean(props.inProgress || props.getTableDataInProgress));
    }, [props.inProgress, props.getTableDataInProgress]);
    if (!props.documentId)
        return null;
    return (React.createElement(ProgressOverlay, { show: props.inProgress || props.getTableDataInProgress, className: classNames("overflow-hidden", props.className), onCancel: props.getTableDataInProgress && props.onCancelTableData
            ? handleCancelGetTableData
            : undefined },
        React.createElement("div", { className: "overflow-hidden grid-rows-auto-1fr", style: containerStyle },
            React.createElement(RowActionsPanel, { disabled: !props.data, rightSidePanel: React.createElement(RightSideDetailsPanel, { openedHeight: height ? height + (props.bottomOffset || 0) : undefined, closedHeight: props.rowActionsPanelHeight, onDataUpdate: props.canEdit && props.data ? handleDataUpdate : undefined, canModifyAttributes: true, onGetPreview: props.data ? handleGetPreview : undefined, tableColumns: detailsPanelTableColumns }), rowActionsPanelHeight: props.rowActionsPanelHeight, isColumnChooserOpen: isColumnChooserOpen, onOpenColumnChooser: props.data ? handleOpenColumnChooser : undefined, settings: settings, onChangeSettings: props.data ? handleChangeSettings : undefined, onSave: props.documentId &&
                    hasChanges &&
                    props.canEdit &&
                    props.readOnlyMode === READ_ONLY_MODE.READ_WRITE &&
                    props.data
                    ? savingCallback(props.documentId)
                    : undefined, onRefresh: props.documentId && props.selectedDocumentObjectIds && props.data
                    ? handleGetTableData
                    : undefined, getSummaryReportObjectValue: getSummaryReportObjectValue }),
            React.createElement("div", { className: "position-relative overflow-hidden", ref: observableRef }, props.data ? (React.createElement("div", { className: "overflow-hidden grid-rows-auto-1fr" },
                React.createElement(DocumentObjectTypesPanel, { panelHeight: SIZES.DOCUMENT_OBJECT_TYPES_PANEL_HEIGHT, documentObjects: props.documentObjects, availableDocumentObjectIds: props.availableDocumentObjectIds, onGetTableData: handleGetTableData, onSelectDocumentObjects: handleSelectDocumentObjects, selectedDocumentObjectIds: props.selectedDocumentObjectIds, gridRef: gridRef, documentName: props.documentName }),
                dataGrid)) : (React.createElement(Welcome, { documentObjects: props.documentObjects, availableDocumentObjectIds: props.availableDocumentObjectIds, onGetTableData: handleGetTableData, onSelectDocumentObjects: handleSelectDocumentObjects, selectedDocumentObjectIds: props.selectedDocumentObjectIds }))))));
}
